import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import { faBackward, faBackwardStep, faForward, faForwardStep, faSort, faSortUp, faSortDown, } from '@fortawesome/free-solid-svg-icons';
import './Grid.css';
import LoadingOverlay from '../loadingoverlay/LoadingOverlay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EllipsedCell from '../ellipsedCell/EllipsedCell';

const parseDateTimeString = (str) => {
  const dateTimeRegex =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?\s*(AM|PM)?)?$/i;

  const m = str.match(dateTimeRegex);
  if (!m) return null;

  let [
    ,
    month,
    day,
    year,
    hour = '0',
    minute = '0',
    second = '0',
    millis = '0',
    ampm,
  ] = m;

  const y = parseInt(year, 10);
  const mo = parseInt(month, 10) - 1;
  const d = parseInt(day, 10);
  let h = parseInt(hour, 10);
  const min = parseInt(minute, 10);
  const sec = parseInt(second, 10);
  const ms = parseInt(millis.padEnd(3, '0'), 10);

  if (ampm) {
    const upper = ampm.toUpperCase();
    if (upper === 'PM' && h < 12) h += 12;
    if (upper === 'AM' && h === 12) h = 0;
  }

  const dateObj = new Date(y, mo, d, h, min, sec, ms);
  return dateObj.getTime();
};

const extractReactElementSortValue = (element) => {
  const p = element.props || {};
  if (p['data-sort'] != null) return p['data-sort'];
  if (p['aria-label'] != null) return p['aria-label'];
  if (p['title'] != null) return p['title'];
  if (p.icon && p.icon.iconName) return p.icon.iconName.toLowerCase();
  return '';
};

const Grid = ({
  headers,
  data,
  rowsPerPage = 10,
  canMultiSelect = false,
  onRowClick,
  onRowDoubleClick,
  onSelectionChange,
  loading = false,
  sortable = false,
  showPagination = true,
  isSummary = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'none' });

  const selectAllRef = useRef();
  const scrollRef = useRef(null);

  const extractSortableValue = (val) => {
    if (val == null) return '';

    if (val instanceof Date) {
      return val.getTime();
    }

    if (typeof val === 'string') {
      const s = val.trim();
      if (!s) return '';

      const timestamp = parseDateTimeString(s);
      if (timestamp !== null) {
        return timestamp;
      }

      return s.toLowerCase();
    }

    if (React.isValidElement(val)) {
      return extractReactElementSortValue(val);
    }

    return val;
  };

  const handleSort = (key) => {
    if (!sortable) return;

    setSortConfig((prev) => {
      // new column clicked → start ascending
      if (prev.key !== key) {
        return { key, direction: 'asc' };
      }

      // same column → cycle asc → desc → none
      if (prev.direction === 'asc') {
        return { key, direction: 'desc' };
      }
      if (prev.direction === 'desc') {
        return { key: null, direction: 'none' }; // reset
      }

      // none → asc
      return { key, direction: 'asc' };
    });
  };


  const sortedData = useMemo(() => {
    if (!sortConfig.key || sortConfig.direction === 'none') return [...data];

    return [...data].sort((a, b) => {
      const aVal = extractSortableValue(a[sortConfig.key] ?? '');
      const bVal = extractSortableValue(b[sortConfig.key] ?? '');

      if (aVal === bVal) return 0;
      return sortConfig.direction === 'asc'
        ? (aVal > bVal ? 1 : -1)
        : (aVal < bVal ? 1 : -1);
    });
  }, [data, sortConfig]);


  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(startIndex + currentData.length, totalItems);

  const displayedHeaders = headers.filter((h) => h.visible !== false);
  const showSelectColumn = canMultiSelect && data.length > 0;


  useEffect(() => {
    if (showSelectColumn && selectAllRef.current) {
      const currentPageIds = currentData.map((row) => row.id);
      const allSelected = currentPageIds.every((id) => selectedIds.has(id));
      const noneSelected = currentPageIds.every((id) => !selectedIds.has(id));

      selectAllRef.current.indeterminate = !allSelected && !noneSelected;
      selectAllRef.current.checked = allSelected && currentPageIds.length > 0;
    }
  }, [selectedIds, currentData, showSelectColumn]);

  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = data.filter((row) => selectedIds.has(row.id));
      onSelectionChange(selectedRows);
    }
  }, [selectedIds, data, onSelectionChange]);

  useEffect(() => {
    setSelectedIds(new Set());
    setCurrentPage(1);
  }, [data]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [currentPage, sortConfig]);

  const handleCheckboxChange = (rowId) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        if (!canMultiSelect) {
          newSet.clear();
        }
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const handleSelectAllChange = () => {
    const currentPageIds = currentData.map((row) => row.id);
    const allSelected = currentPageIds.every((id) => selectedIds.has(id));

    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        currentPageIds.forEach((id) => newSet.delete(id));
      } else {
        currentPageIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  return (
    <div className="custom-grid-container">
      <div className="custom-table-wrapper">
        <table className="custom-table">
          <thead>
            <tr>
              {showSelectColumn && (
                <th style={{ width: '40px', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    ref={selectAllRef}
                    onChange={handleSelectAllChange}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {displayedHeaders.map((header, idx) => {
                const isSorted = sortConfig.key === header.key && sortConfig.direction !== 'none';
                const icon = isSorted
                  ? (sortConfig.direction === 'asc' ? faSortUp : faSortDown)
                  : faSort;

                return (
                  <th
                    key={idx}
                    style={{
                      width: header.width,
                      textAlign: header.alignHeader || 'left',
                      cursor: 'default'
                    }}
                    onClick={sortable ? () => handleSort(header.key) : undefined}
                    scope="col"
                    aria-sort={
                      sortConfig.key === header.key
                        ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending')
                        : 'none'
                    }
                  >
                    <div className="header-content">
                      <div className="header-label">
                        {header.label}
                      </div>

                      {sortable && (
                        <button
                          type='button'
                          className={`sort-btn ${isSorted ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();    // prevent row click/double-click
                            handleSort(header.key);
                          }}
                          aria-pressed={isSorted}
                          title={`Sort by ${header.label}`}
                        >
                          <FontAwesomeIcon
                            icon={icon}
                            className={isSorted ? 'active-sort-icon' : 'inactive-sort-icon'}
                          />
                        </button>
                      )}
                    </div>
                  </th>
                );
              })}



            </tr>
          </thead>
          <tbody className="custom-table-body-wrapper">
            {currentData.length === 0 ? (
              <tr>
                <td
                  colSpan={displayedHeaders.length + (showSelectColumn ? 1 : 0)}
                  className="empty-state"
                >
                  No data available
                </td>
              </tr>
            ) : (
              currentData.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                  onDoubleClick={() => onRowDoubleClick?.(row)}
                  className={`${onRowClick || onRowDoubleClick ? 'table-row' : ''} ${selectedIds.has(row.id) ? 'selected-row' : ''}`}
                >
                  {showSelectColumn && (
                    <td style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => handleCheckboxChange(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select row ${row.id}`}
                      />
                    </td>
                  )}
                  {displayedHeaders.map((header, idx) => (
                    <td key={idx} style={{ textAlign: header.alignContent || 'left' }}>
                      <EllipsedCell value={row[header.key]} scrollContainerRef={scrollRef} />
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {loading && <LoadingOverlay />}
      </div>

      {totalItems > 0 && showPagination && (
        <div className={`pagination-controls-container ${isSummary ? 'summary-mode' : ''}`}>
          <div className="pagination-controls">
            <span className="pagination-text">
              Items {startItem}-{endItem} of {totalItems}
            </span>
            {totalPages > 1 && (<>
              <Button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="btn-transperant"
              >
                <FontAwesomeIcon icon={faBackwardStep} />
              </Button>
              <Button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="btn-transperant"
              >
                <FontAwesomeIcon icon={faBackward} />
              </Button>
              <span className='pagination-text'>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="btn-transperant"
              >
                <FontAwesomeIcon icon={faForward} />
              </Button>
              <Button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="btn-transperant"
              >
                <FontAwesomeIcon icon={faForwardStep} />
              </Button>
            </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Grid;
